import { Checkbox, Radio } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ActionButton,
  SelectMultiple,
  Select,
} from '~/components';
import { handlePaste } from '~/helpers/pasteHelper';
import { getUserProfile } from '~/helpers/userProfile';
import { DeleteOutlineIcon } from '~/icons';
import { colors } from '~/styles/colors';

type ClientItemProps = {
  client: { CLIENT_ID: number, NAME: string };
  userType: string;
  supervisors:{[CLIENT_ID: string]: {
    USER_ID: string;
    NAME: string;
    UNITS: number[];
    STATES: number[];
  }[]},
  handleClientTypeChange: (e: any, clientId: string) => void;
  checkClients: (clients: any) => void;
  setClients: (clients: any) => void;
  cProfiles: { [clientId: string]: string };
  cProg: { [clientId: string]: boolean };
  clients: any;
  unitsList: null|({ UNIT_NAME: string, UNIT_ID: number, STATE_ID: number, STATE_NAME: string, checked?: boolean }[]);
  onChangeUnitsSelection: () => void;
}

export const ClientItem = (props: ClientItemProps): JSX.Element => {
  const { t } = useTranslation();
  const [profile] = useState(getUserProfile);
  const [programacaoMultipla, setProgramacaoMultipla] = useState(false);
  const [listUnits, setListUnits] = useState(props.unitsList);
  const [listStates, setListStates] = useState<{
    STATE_ID: any;
    STATE_NAME: any;
    checked: boolean;
  }[]>([]);
  const [listSupervisor, setListSupervisor] = useState<{
    USER_ID: string;
    NAME: string;
    UNITS: number[];
    STATES: number[];
    checked?: boolean;
  }[]>((props.supervisors[props.client.CLIENT_ID] || []).map((item) => ({ ...item, checked: false })) || []);
  const toggleCheckbox = () => {
    setProgramacaoMultipla(!programacaoMultipla);

    props.cProg[String(props.client.CLIENT_ID)] = !programacaoMultipla;
  };

  const {
    client, userType, handleClientTypeChange, checkClients, setClients, cProfiles, clients, cProg, supervisors,
  } = props;

  useEffect(() => {
    if (cProg[String(client.CLIENT_ID)]) {
      setProgramacaoMultipla(cProg[String(client.CLIENT_ID)]);
    } else {
      setProgramacaoMultipla(false);
    }
  }, []);

  async function getStates() {
    const stateMap = new Map<number, { STATE_ID: number; STATE_NAME: string; checked: boolean }>();

    let hasCheckedUnit = false;

    props.unitsList?.forEach(({ STATE_ID, STATE_NAME, checked }) => {
      if (!stateMap.has(STATE_ID)) {
        stateMap.set(STATE_ID, { STATE_ID, STATE_NAME, checked: false });
      }
      if (checked) {
        stateMap.get(STATE_ID)!.checked = true;
        hasCheckedUnit = true;
      }
    });

    if (!hasCheckedUnit) {
      stateMap.forEach((state) => (state.checked = true));
    }

    setListStates(Array.from(stateMap.values()));
  }

  useEffect(() => {
    setListUnits(props.unitsList);
    getStates();
  }, [props.unitsList]);

  function changeMultipleSelection(item, list, isState) {
    if (item) {
      item.checked = !item.checked;
      if (isState && listUnits?.length && props.unitsList) {
        const selectedStates: number[] = (listStates ?? []).filter((x) => x.checked).map((state) => (state.STATE_ID));
        const newArray: {
          UNIT_NAME: string;
          UNIT_ID: number;
          STATE_ID: number;
          STATE_NAME: string;
          checked?: boolean;
        }[] = [];

        for (const unit of props.unitsList) {
          if (selectedStates.includes(unit.STATE_ID)) {
            unit.checked = true;
            newArray.push({ ...unit, checked: true });
          }
        }
        setListUnits(newArray);
        const newArraySupervisor = [] as { USER_ID: string; NAME: string; UNITS: number[]; STATES: number[]; checked?: boolean | undefined; }[];
        const listSupervisorAll = props.supervisors[props.client.CLIENT_ID] || [];
        for (const supervisor of listSupervisorAll) {
          if (supervisor.STATES.some((stateId) => selectedStates.includes(stateId))) {
            newArraySupervisor.push(supervisor);
          }
        }
        setListSupervisor(newArraySupervisor);
      }
    } else {
      for (const elem of list) { elem.checked = false; }
      if (isState && props.unitsList) {
        for (const unit of props.unitsList) {
          unit.checked = false;
        }
      }
    }
    props.onChangeUnitsSelection();
  }

  function changeMultipleSelectionSupervisor(item, list) {
    if (item) {
      item.checked = !item.checked;
    } else {
      for (const elem of list) {
        elem.checked = false;
      }
    }
    setListSupervisor(list);
    const selectedSupervisors = list.filter((supervisor) => supervisor.checked);
    const selectedUnitIds = new Set(
      selectedSupervisors.flatMap((supervisor) => supervisor.UNITS || []),
    );
    listUnits?.forEach((unit) => {
      unit.checked = selectedUnitIds.has(unit.UNIT_ID);
    });
    props.onChangeUnitsSelection();
  }

  function changeMultipleSelectionPersona2(item, list) {
    for (const elem of list) { elem.checked = false; }

    if (item) {
      item.checked = !item.checked;
    }
    setListUnits(list);
    props.onChangeUnitsSelection();
  }

  function selectAllOptions(list: { checked?: boolean }[], isState) {
    const shouldBeChecked = list.some((el) => !el.checked);
    if (shouldBeChecked) {
      for (const elem of list) {
        elem.checked = true;
      }
      if (isState && props.unitsList?.length) {
        for (const unit of props.unitsList) {
          unit.checked = true;
        }
        setListUnits(props.unitsList);
      }
    } else {
      for (const elem of list) {
        elem.checked = false;
      }
      if (isState && props.unitsList?.length) {
        for (const unit of props.unitsList) {
          unit.checked = false;
        }
        setListUnits(props.unitsList);
      }
    }

    props.onChangeUnitsSelection();
  }

  function selectAllOptionsSupervisor(list) {
    const allSelected = list.every((item) => item.checked);
    if (allSelected) {
      list.forEach((item) => {
        item.checked = false;
      });
      listUnits?.forEach((unit) => {
        unit.checked = false;
      });
    } else {
      list.forEach((item) => {
        item.checked = true;
      });
      const allUnitIds = new Set<number>();
      list.forEach((supervisor) => {
        (supervisor.UNITS || []).forEach((unitId) => {
          allUnitIds.add(unitId);
        });
      });
      listUnits?.forEach((unit) => {
        unit.checked = allUnitIds.has(unit.UNIT_ID);
      });
    }
    setListSupervisor([...list]);
    props.onChangeUnitsSelection();
  }

  function selectTypeProfile(userType) {
    if (userType === 'mantenedor') {
      return <Radio value="[M]">{t('Manutenção')}</Radio>;
    }
    if (['instalador', 'mantenedorv2', 'persona2'].some((x) => x === userType)) {
      return (
        <>
        </>
      );
    }
    return (
      <>
        <Radio value="[U]">{t('usuario')}</Radio>
        <Radio value="[C]">{t('Admin')}</Radio>
      </>
    );
  }
  function setUnits(pastedItems) {
    const matchingUnits = (listUnits || []).filter((unit) => pastedItems?.includes(unit.UNIT_NAME)).map((unit) => unit);
    const onlyIds = matchingUnits.map((item) => item.UNIT_ID);
    setListUnits((listUnits || []).map((item) => {
      if (userType !== 'persona2' && onlyIds.includes(item.UNIT_ID)) item.checked = true;
      return item;
    }));
  }

  return (
    <div>
      <div style={{
        width: 400, display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '5px',
      }}
      >
        <span style={{ width: 150 }}>
          {client.NAME}
          :
        </span>

        <Radio.Group onChange={(e) => handleClientTypeChange(e, String(client.CLIENT_ID))} value={cProfiles[String(client.CLIENT_ID)]}>
          {selectTypeProfile(userType)}
        </Radio.Group>
        <ActionButton
          onClick={() => {
            const newClients = clients.filter((x) => x !== client);
            setClients(newClients);
            checkClients(newClients);
          }}
          variant="red-inv"
        >
          <DeleteOutlineIcon colors={colors.Black} />
        </ActionButton>
      </div>
      {(userType !== 'persona2' && listUnits && listUnits.length > 0) && (
        <div
          style={{
            width: 400, display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px',
          }}
          onPaste={(e) => handlePaste(e, setUnits)}
        >
          <SelectMultiple
            propLabel="STATE_NAME"
            options={listStates}
            values={listStates.filter((x) => x.checked)}
            haveFuzzySearch
            haveSelectAll
            selectAllOptions={() => selectAllOptions(listStates, true)}
            onSelect={(item) => changeMultipleSelection(item, listStates, true)}
            placeholder={listStates.some((x) => x.checked) ? t('estados') : `${t('filtrePeloEstado')}`}
            style={{ width: '280px' }}
          />
        </div>
      )}
      {(userType !== 'persona2' && listUnits && listUnits.length > 0 && listSupervisor.length > 0) && (
        <div
          style={{
            width: 400, display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px',
          }}
          onPaste={(e) => handlePaste(e, setUnits)}
        >
          <SelectMultiple
            propLabel="NAME"
            options={listSupervisor}
            values={listSupervisor ? listSupervisor.filter((item) => item.checked) : []}
            haveFuzzySearch
            haveSelectAll
            selectAllOptions={() => selectAllOptionsSupervisor(listSupervisor)}
            onSelect={(item) => changeMultipleSelectionSupervisor(item, listSupervisor)}
            placeholder={listSupervisor.some((x) => x.checked) ? t('responsavel') : `${t('filtrePeloResponsavel')}`}
            style={{ width: '280px' }}
          />
        </div>
      )}
      {
          (profile.manageAllClients || profile.permissions.CLIENT_MANAGE) && cProfiles[String(client.CLIENT_ID)] === '[C]' && userType === 'cliente' && (
            <div style={{ marginTop: 10, marginBottom: 10 }}>
              <Checkbox checked={programacaoMultipla} onChange={toggleCheckbox} value={cProg[String(client.CLIENT_ID)]}>{t('possuiPermissaoControlarAutomacao')}</Checkbox>
            </div>
          )
        }
      {(userType !== 'persona2' && listUnits && listUnits.length > 0) && (
        <div
          style={{
            width: 400, display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px',
          }}
          onPaste={(e) => handlePaste(e, setUnits)}
        >
          <SelectMultiple
            propLabel="UNIT_NAME"
            options={listUnits}
            values={listUnits.filter((x) => x.checked)}
            haveFuzzySearch
            haveSelectAll
            selectAllOptions={() => selectAllOptions(listUnits, false)}
            onSelect={(item) => changeMultipleSelection(item, listUnits, false)}
            placeholder={listUnits.some((x) => x.checked) ? t('unidades') : `${t('Selecione as unidades')}`}
            style={{ width: '280px' }}
          />
        </div>
      )}

      {(userType === 'persona2' && listUnits && listUnits.length > 0) && (
        <div
          style={{
            width: 280, display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px',
          }}
        >
          <Select
            haveFuzzySearch
            options={listUnits}
            placeholder={t('Selecione as unidades')}
            propLabel="UNIT_NAME"
            value={listUnits.find((x) => x.checked)}
            onSelect={(item) => {
              changeMultipleSelectionPersona2(item, listUnits);
            }}
          />
        </div>
      )}
    </div>
  );
};
